/**
 * enroll-tablet — issue a shared-tablet enrollment QR from the web (OPEN-035).
 *
 * Master spec §7: "Tenant admin enrolls device via QR code from web console." The server half
 * is `POST /v1/tenants/:id/devices/enroll` (MANAGE_USERS, own tenant only), which returns
 * `{ qrPayload }` as a JSON OBJECT. The tablet's scanner (`parseQrPayload` in
 * mobile/src/auth/shared-device/qr-enrollment.ts) reads the QR text with `atob` and then
 * `JSON.parse`, so the code must carry standard base64 of that JSON — not base64url, which
 * `atob` rejects, and not the raw JSON.
 *
 * The payload's `enrollmentToken` is a single-use credential that hands out the tablet's roster
 * keys. It is returned to the caller and nowhere else: no console, no storage, no query cache.
 */

import { authedFetch, type WebApiOpts } from '@lodex-pro/web-shared';

/** The decoded QR body, as the API returns it and the tablet validates it. */
export interface QrEnrollmentPayload {
  readonly v: 1;
  readonly tenantId: string;
  readonly deviceId: string;
  readonly deviceName: string;
  readonly enrollmentToken: string;
}

export interface IssuedEnrollment {
  /** The exact text to draw as the QR code. */
  readonly qrText: string;
  readonly deviceName: string;
  /** Local clock time (ms) after which the tablet can no longer use the code. */
  readonly expiresAtMs: number;
}

/** The server's token and Redis TTL (enroll.ts / enrollment-token.ts): 10 minutes. */
export const DEFAULT_ENROLLMENT_LIFETIME_MS = 10 * 60 * 1000;

/** Why an enrollment could not be issued, in words a dispatcher can act on. */
export class EnrollTabletError extends Error {
  constructor(readonly status: number | null) {
    super(
      status === 403
        ? 'You do not have permission to add tablets.'
        : 'Could not make a code. Try again.',
    );
    this.name = 'EnrollTabletError';
  }
}

/**
 * The QR text for a payload: standard base64 of its JSON. Any character outside printable
 * ASCII is written as a JSON `\u` escape first, because `btoa` throws on it and the tablet's
 * `atob` would otherwise hand `JSON.parse` the raw UTF-8 bytes (a "Café" tablet would arrive
 * misspelled). The escape decodes back to the same string on the tablet.
 */
export function qrTextFor(payload: QrEnrollmentPayload): string {
  const json = JSON.stringify(payload);
  let ascii = '';
  for (let i = 0; i < json.length; i++) {
    const code = json.charCodeAt(i);
    ascii += code > 0x7e ? `\\u${code.toString(16).padStart(4, '0')}` : json.charAt(i);
  }
  return btoa(ascii);
}

/**
 * How long the code is good for, from the token's own `iat`/`exp`. A duration rather than
 * `exp` itself, so a browser whose clock is off still counts down the right ten minutes.
 * Falls back to the server's fixed 10 minutes if the token cannot be read.
 */
export function enrollmentLifetimeMs(token: string): number {
  try {
    const segment = token.split('.')[1] ?? '';
    const base64 = segment.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
    const claims = JSON.parse(atob(padded)) as { iat?: unknown; exp?: unknown };
    if (typeof claims.iat === 'number' && typeof claims.exp === 'number') {
      const ms = (claims.exp - claims.iat) * 1000;
      if (ms > 0 && ms <= DEFAULT_ENROLLMENT_LIFETIME_MS) return ms;
    }
  } catch {
    // Unreadable token: use the server's documented lifetime below.
  }
  return DEFAULT_ENROLLMENT_LIFETIME_MS;
}

/**
 * Issue an enrollment QR for a tablet named `deviceName`.
 *
 * The tenant id is read fresh from `GET /v1/users/me/tenants` on every call: the route 403s
 * TENANT_MISMATCH on anything but the caller's ACTIVE tenant, and a cached id would go stale
 * after a tenant switch.
 */
export async function enrollTablet(
  opts: WebApiOpts,
  deviceName: string,
  nowMs: () => number = Date.now,
): Promise<IssuedEnrollment> {
  const me = await authedFetch(opts, '/v1/users/me/tenants');
  if (!me.ok) throw new EnrollTabletError(me.status);
  const { activeTenantId } = (await me.json()) as { activeTenantId: string | null };
  if (activeTenantId === null) throw new EnrollTabletError(null);

  const res = await authedFetch(
    opts,
    `/v1/tenants/${encodeURIComponent(activeTenantId)}/devices/enroll`,
    { method: 'POST', body: JSON.stringify({ deviceName }) },
  );
  if (!res.ok) throw new EnrollTabletError(res.status);
  const { qrPayload } = (await res.json()) as { qrPayload: QrEnrollmentPayload };

  return {
    qrText: qrTextFor(qrPayload),
    deviceName: qrPayload.deviceName,
    expiresAtMs: nowMs() + enrollmentLifetimeMs(qrPayload.enrollmentToken),
  };
}
